import { keyframes } from "@emotion/react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useRef, useState } from "react";

import MentionDropdown from "../components/MentionDropdown";
import { useMention } from "../context/MentionContext";
import { useMentionInput } from "../Hooks/useMentionInput";

const bounce = keyframes`
  0%, 100% { transform: translateY(0);}
  50% { transform: translateY(-3px);}
`;

export const TimelineCard = ({
  title = "Timeline",
  timelineData,
  initiallyVisible = 3,
  getIconLetter,
  fields = [],
  dropdownOptions = [],
  handleAddNote = () => {},
  onDropdownChange = () => {},
  dropdownKey = "dropdownValue",
}) => {
  const theme = useTheme();
  const inputRef = useRef(null);

  const [noteInputs, setNoteInputs] = useState({});
  const [addedByInputs, setAddedByInputs] = useState({});
  const [expandedNotes, setExpandedNotes] = useState({});
  const [expanded, setExpanded] = useState(false); 
  const { getMentionChangeHandler } = useMentionInput();

  const entriesToShow = expanded
    ? [...timelineData].reverse()
    : [...timelineData].reverse().slice(0, initiallyVisible);

  return (
    <Card sx={{ p: 4, mb: 6, borderRadius: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {title}
      </Typography>

      {expanded && timelineData.length > initiallyVisible && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setExpanded(false)}
            sx={{ ":hover": { bgcolor: "transparent" } }}
          >
            Show Less
          </Button>
        </Box>
      )}

      <Box sx={{ position: "relative", mt: 4 }}>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            position: "absolute",
            left: 30,
            top: 0,
            height: "100%",
            borderColor: "#e0e0e0",
          }}
        />

        {entriesToShow.map((entry, idx) => (
          <Box key={entry._id || idx} sx={{ position: "relative", mb: 4 }}>
            {/* Timeline dot */}
            <Box
              sx={{
                position: "absolute",
                top: 6,
                width: 20,
                height: 20,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.dark
                    : theme.palette.primary.main,
              }}
            >
              <Typography sx={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>
                {getIconLetter(entry)}
              </Typography>
            </Box>

            <Box sx={{ ml: 7 }}>
              <Typography variant="caption" color="text.secondary">
                {new Date(entry.timestamp).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </Typography>

              <Card
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderLeft: `5px solid ${theme.palette.primary.main}`,
                  borderRadius: 2,
                }}
              >
                <Grid container spacing={3}>
                  {/* LEFT SIDE */}
                  <Grid item xs={12} md={6}>
                    {fields.map((field) => (
                      <Box key={field.key} sx={{ display: "flex", mb: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ minWidth: 140, fontWeight: "bold" }}
                        >
                          {field.label || field.key}:
                        </Typography>
                        <Typography
                          variant={field.variant || "body2"}
                          fontWeight={field.bold ? "bold" : "normal"}
                        >
                          {field.render
                            ? field.render(entry[field.key])
                            : entry[field.key]}
                          {field.suffix && entry[field.key] != null
                            ? ` ${field.suffix}`
                            : ""}
                        </Typography>
                      </Box>
                    ))}

                    {dropdownOptions.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Select
                          size="small"
                          value={entry[dropdownKey] || ""}
                          onChange={(e) =>
                            onDropdownChange(e.target.value, entry)
                          }
                          sx={{ width: 180 }}
                        >
                          <MenuItem value="" disabled>
                            Select an action
                          </MenuItem>
                          {dropdownOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}
                  </Grid>

                  {/* RIGHT SIDE — NOTES */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Notes:
                    </Typography>

                    {entry.notes?.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {(expandedNotes[entry._id]
                          ? entry.notes
                          : entry.notes.slice(0, 1)
                        ).map((note) => (
                          <Card
                            key={note._id}
                            variant="outlined"
                            sx={{ p: 2, borderRadius: 2 }}
                          >
                            <Typography variant="body2">{note.text}</Typography>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption">
                                {new Date(note.createdAt).toLocaleString()}
                              </Typography>
                              <Typography variant="caption">
                                {note.updatedBy}
                              </Typography>
                            </Box>
                          </Card>
                        ))}

                        {entry.notes.length > 1 && (
                          <Button
                            size="small"
                            onClick={() =>
                              setExpandedNotes((prev) => ({
                                ...prev,
                                [entry._id]: !prev[entry._id],
                              }))
                            }
                          >
                            {expandedNotes[entry._id]
                              ? "Show Less"
                              : "Show More"}
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No notes yet.
                      </Typography>
                    )}

                    {/* ADD NOTE */}
                    <Box sx={{ mt: 2, position: "relative" }}>
                      <TextField
                        inputRef={inputRef}
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Add a note… (use @ to tag)"
                        value={noteInputs[entry._id] || ""}
                        onChange={getMentionChangeHandler(
                          noteInputs[entry._id] || "",
                          (val) =>
                            setNoteInputs((prev) => ({
                              ...prev,
                              [entry._id]: val,
                            })),
                          inputRef
                        )}
                      />

                      <MentionDropdown />

                      <Box display="flex" marginTop={2}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            handleAddNote(
                              entry._id,
                              noteInputs[entry._id] || ""
                            );
                            setNoteInputs((prev) => ({
                              ...prev,
                              [entry._id]: "",
                            }));
                            setAddedByInputs((prev) => ({
                              ...prev,
                              [entry._id]: "",
                            }));
                          }}
                          disabled={!String(noteInputs[entry._id] || "").trim()}
                        >
                          Add Note
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Box>
          </Box>
        ))}
      </Box>

      {!expanded && timelineData.length > initiallyVisible && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button onClick={() => setExpanded(true)}>
            Show More
            <ExpandMoreIcon sx={{ animation: `${bounce} 1.2s infinite` }} />
          </Button>
        </Box>
      )}
    </Card>
  );
};
